import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Truck, DollarSign, Calendar, Target } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const EuropeLogisticsArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-green-500 text-white">
                <Truck className="h-3 w-3 mr-1" />
                {t('article.europelogistics.badge')}
              </Badge>
              <span className="text-gray-500">{t('article.europelogistics.date')}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.europelogistics.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.europelogistics.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://d1dcnte8mfzkpv.cloudfront.net/uploads/2024/09/1b8bd9dfdeec192713b40e1d93d1.jpg"
              alt="EU investigation into Chinese e-commerce subsidies" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                  {t('europelogistics.stats.growth.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('europelogistics.stats.growth.value')}</div>
                <p className="text-sm text-gray-600">{t('europelogistics.stats.growth.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                  {t('europelogistics.stats.timeline.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('europelogistics.stats.timeline.value')}</div>
                <p className="text-sm text-gray-600">{t('europelogistics.stats.timeline.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Target className="h-5 w-5 mr-2 text-emerald-600" />
                  {t('europelogistics.stats.driver.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-emerald-600">{t('europelogistics.stats.driver.value')}</div>
                <p className="text-sm text-gray-600">{t('europelogistics.stats.driver.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('europelogistics.section1.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('europelogistics.section1.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('europelogistics.section2.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('europelogistics.section2.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('europelogistics.section2.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('europelogistics.section3.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('europelogistics.section3.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('europelogistics.section3.list')}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('europelogistics.section4.title')}</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('europelogistics.section4.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('europelogistics.section4.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('europelogistics.section5.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('europelogistics.section5.content')}
            </p>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('europelogistics.section5.list')}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('europelogistics.section6.title')}</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('europelogistics.section6.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('europelogistics.section6.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('europelogistics.section7.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('europelogistics.section7.content')}
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('europelogistics.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://www.indexbox.io/blog/truck-europe-market-overview-2024-4/', '_blank')}
              >
                View Original Source
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default EuropeLogisticsArticle;